// src/components/RepresentationEdit.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import Select from "./ui/Select";
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import axiosInstance from "../utils/axiosInstance";
import { updateRepresentation } from "../actions/representationActions";
import { fetchCostumers } from "../actions/authActions";
import { LazyLoading } from "./ui/LazyLoading";
import { UploadInputInterface } from "./ui/UploadInputInterface";
import toast from "react-hot-toast";
import { translateApiMessage } from "../utils/formErrors";
import { toastApiError, toastErrors } from "../utils/toastErrors";
import { REPRESENTATION_LABELS, validateRepresentation } from "../utils/recordValidation";
import {
  BadgeCheck,
  CalendarDays,
  FileText,
  FileUp,
  Hash,
  Loader2,
  Users,
  UserRound,
  X,
} from "lucide-react";

const cx = (...classes) => classes.filter(Boolean).join(" ");

const firstErr = (v) => (Array.isArray(v) ? v[0] : v);

const extractErrorData = (err) =>
  err?.response?.data ?? { detail: err?.message || "خطای نامشخص" };

/* ---------- Field (same system as your previous one) ---------- */
const Field = ({
  id,
  name,
  label,
  placeholder,
  value,
  onChange,
  type = "text",
  required,
  icon: Icon,
  inputMode,
}) => {
  return (
    <div className="space-y-1.5">
      <label
        htmlFor={id}
        className="block text-sm font-medium text-slate-700 text-right"
      >
        {label}
        {required ? <span className="text-rose-500"> *</span> : null}
      </label>

      <div className="relative">
        {Icon ? (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
            <Icon size={18} />
          </span>
        ) : null}

        <input
          id={id}
          name={name}
          type={type}
          inputMode={inputMode}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          required={required}
          className={cx(
            "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-right text-slate-900",
            "placeholder:text-slate-400 shadow-sm",
            "focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary",
            Icon ? "pr-10" : "pr-3"
          )}
        />
      </div>
    </div>
  );
};

/* ---------- SelectField matching Field look ---------- */
const SelectField = ({
  id,
  label,
  required,
  icon: Icon,
  isMulti,
  isLoading,
  options,
  value,
  onChange,
  placeholder,
  error,
}) => {
  const portalTarget =
    typeof document !== "undefined" ? document.body : undefined;

  const selectStyles = useMemo(
    () => ({
      control: (base, state) => ({
        ...base,
        minHeight: "44px",
        borderRadius: "0.75rem",
        borderColor: state.isFocused ? "#3b82f6" : "#e2e8f0", // primary / slate-200
        boxShadow: state.isFocused ? "0 0 0 2px rgba(59,130,246,0.15)" : "none",
        backgroundColor: "#ffffff",
        paddingRight: Icon ? "34px" : "6px",
        paddingLeft: "6px",
        direction: "rtl",
        textAlign: "right",
        fontFamily: "Vazirmatn, sans-serif",
      }),
      valueContainer: (b) => ({ ...b, paddingTop: 2, paddingBottom: 2 }),
      placeholder: (base) => ({
        ...base,
        color: "#94a3b8",
        textAlign: "right",
      }),
      singleValue: (base) => ({
        ...base,
        color: "#0f172a",
        textAlign: "right",
      }),
      input: (base) => ({ ...base, textAlign: "right" }),
      menu: (base) => ({
        ...base,
        direction: "rtl",
        borderRadius: "0.75rem",
        overflow: "hidden",
        boxShadow:
          "0 10px 15px -3px rgba(0,0,0,0.08), 0 4px 6px -4px rgba(0,0,0,0.06)",
      }),
      option: (base, state) => ({
        ...base,
        textAlign: "right",
        backgroundColor: state.isSelected
          ? "rgba(59,130,246,1)"
          : state.isFocused
          ? "rgba(241,245,249,1)"
          : "#fff",
        color: state.isSelected ? "#fff" : "#0f172a",
      }),
      multiValue: (base) => ({
        ...base,
        backgroundColor: "rgba(59,130,246,0.08)",
        borderRadius: "0.65rem",
      }),
      multiValueLabel: (base) => ({ ...base, color: "#0f172a" }),
      multiValueRemove: (base) => ({
        ...base,
        borderRadius: "0.65rem",
        ":hover": { backgroundColor: "rgba(59,130,246,1)", color: "#fff" },
      }),
    }),
    [Icon]
  );

  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-slate-700 text-right">
        {label}
        {required ? <span className="text-rose-500"> *</span> : null}
      </label>

      <div className="relative">
        {Icon ? (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 z-10 pointer-events-none">
            <Icon size={18} />
          </span>
        ) : null}

        <Select
          inputId={id}
          name={id}
          isMulti={!!isMulti}
          isLoading={!!isLoading}
          isDisabled={!!isLoading}
          options={options}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          isClearable
          styles={selectStyles}
          menuPortalTarget={portalTarget}
          menuPosition={portalTarget ? "fixed" : "absolute"}
        />
      </div>

      {error ? (
        <div className="text-rose-700 text-sm mt-1 text-right">{error}</div>
      ) : null}
    </div>
  );
};

export default function RepresentationEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { costumerList = [], customersLoading } = useSelector(
    (state) => state.costumers || {}
  );

  const [formData, setFormData] = useState({
    representi: [],
    representor: [],
    applicant: "",
    start_date: "",
    end_date: "",
    another_deligation: false,
    representor_dismissal: false,
    representation_summary: "",
    doc_number: "",
    verification_code: "",
    file: null,
  });

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [serverErrors, setServerErrors] = useState({});

  const customerOptions = useMemo(
    () =>
      (costumerList || []).map((c) => ({
        value: String(c.id),
        label: c.full_name,
      })),
    [costumerList]
  );

  const multiValue = (ids) =>
    customerOptions.filter((o) => (ids || []).includes(o.value));
  const singleValue = (idVal) =>
    customerOptions.find((o) => o.value === idVal) || null;

  const fieldError = (key) =>
    serverErrors?.[key] ? translateApiMessage(firstErr(serverErrors[key])) : "";

  useEffect(() => {
    dispatch(fetchCostumers());
  }, [dispatch]);

  useEffect(() => {
    const fetchOne = async () => {
      setLoading(true);
      try {
        const { data } = await axiosInstance.get(`/representations/${id}/`);
        setFormData({
          representi: (data.principal || []).map((c) => String(c.id)),
          representor: (data.attorney || []).map((c) => String(c.id)),
          applicant: data.applicant_info?.id
            ? String(data.applicant_info.id)
            : "",
          start_date: data.start_date || "",
          end_date: data.end_date || "",
          another_deligation: !!data.another_deligation,
          representor_dismissal: !!data.representor_dismissal,
          representation_summary: data.representation_summary || "",
          doc_number: data.doc_number?.toString() || "",
          verification_code: data.verification_code?.toString() || "",
          file: null, // only set when user selects a new file
        });
      } catch (err) {
        console.error(err);
        toastApiError(err, {}, "دریافت اطلاعات وکالت‌نامه ناموفق بود.");
      } finally {
        setLoading(false);
      }
    };

    fetchOne();
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleStartDateChange = (val) =>
    setFormData((p) => ({
      ...p,
      start_date: val ? val.format("YYYY-MM-DD") : "",
    }));

  const handleEndDateChange = (val) =>
    setFormData((p) => ({
      ...p,
      end_date: val ? val.format("YYYY-MM-DD") : "",
    }));

  const handleFileChange = (e) =>
    setFormData((p) => ({ ...p, file: e?.target?.files?.[0] || null }));


  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerErrors({});
    const { lines: problems, fields: problemFields } = validateRepresentation(formData);
    if (problems.length) {
      setServerErrors(problemFields);
      toastErrors(problems);
      return;
    }
    setSubmitting(true);

    const payload = new FormData();
    formData.representi.forEach((rid) => payload.append("representi", rid));
    formData.representor.forEach((rid) => payload.append("representor", rid));
    payload.append("applicant", formData.applicant || "");
    payload.append("start_date", formData.start_date || "");
    payload.append("end_date", formData.end_date || "");
    payload.append(
      "another_deligation",
      formData.another_deligation ? "true" : "false"
    );
    payload.append(
      "representor_dismissal",
      formData.representor_dismissal ? "true" : "false"
    );
    payload.append(
      "representation_summary",
      formData.representation_summary || ""
    );
    payload.append("doc_number", String(formData.doc_number ?? ""));
    payload.append(
      "verification_code",
      String(formData.verification_code ?? "")
    );
    if (formData.file instanceof File) payload.append("file", formData.file);

    try {
      await dispatch(updateRepresentation(id, payload));
      toast.success("تغییرات وکالت‌نامه ذخیره شد.");
      navigate("/representations");
    } catch (err) {
      setServerErrors(extractErrorData(err));
      toastApiError(err, REPRESENTATION_LABELS, "ذخیره تغییرات وکالت‌نامه ناموفق بود.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LazyLoading />;

  return (
    <div className="mt-10 sm:mt-16 md:mt-20 lg:mt-24 xl:mt-28 px-3 sm:px-6 font-vazirmatn">
      <form onSubmit={handleSubmit} noValidate dir="rtl" className="max-w-3xl mx-auto">
        <div className="bg-white/95 backdrop-blur rounded-2xl shadow-lg border border-slate-200/80 overflow-hidden">
          {/* Header */}
          <div className="px-5 sm:px-7 py-5 border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
            <div className="flex flex-row-reverse items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => navigate("/representations")}
                className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 transition"
              >
                <X size={18} />
                بازگشت
              </button>

              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                  <BadgeCheck size={22} />
                </div>
                <div className="text-right">
                  <h2 className="text-lg sm:text-xl font-extrabold text-slate-800">
                    ویرایش وکالت‌نامه #{id}
                  </h2>
                  <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
                    تغییرات را اعمال کنید و سپس ذخیره را بزنید.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="px-5 sm:px-7 py-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SelectField
                id="representi"
                label="موکل‌ها"
                required
                icon={Users}
                isMulti
                isLoading={customersLoading}
                options={customerOptions}
                value={multiValue(formData.representi)}
                onChange={(opts) =>
                  setFormData((p) => ({
                    ...p,
                    representi: (opts || []).map((o) => o.value),
                  }))
                }
                placeholder="انتخاب موکل‌ها"
                error={fieldError("representi")}
              />

              <SelectField
                id="representor"
                label="وکیل‌ها"
                required
                icon={Users}
                isMulti
                isLoading={customersLoading}
                options={customerOptions}
                value={multiValue(formData.representor)}
                onChange={(opts) =>
                  setFormData((p) => ({
                    ...p,
                    representor: (opts || []).map((o) => o.value),
                  }))
                }
                placeholder="انتخاب وکیل‌ها"
                error={fieldError("representor")}
              />

              <SelectField
                id="applicant"
                label="درخواست‌دهنده"
                icon={UserRound}
                isLoading={customersLoading}
                options={customerOptions}
                value={singleValue(formData.applicant)}
                onChange={(opt) =>
                  setFormData((p) => ({
                    ...p,
                    applicant: opt ? opt.value : "",
                  }))
                }
                placeholder="انتخاب درخواست‌دهنده"
                error={fieldError("applicant")}
              />

              <Field
                id="doc_number"
                name="doc_number"
                label="شماره سند"
                placeholder="مثلاً 12345"
                value={formData.doc_number}
                onChange={handleChange}
                required
                icon={Hash}
                inputMode="numeric"
              />

              <Field
                id="verification_code"
                name="verification_code"
                label="کد تصدیق"
                placeholder="مثلاً 98765"
                value={formData.verification_code}
                onChange={handleChange}
                required
                icon={Hash}
                inputMode="numeric"
              />

              {/* Start Date */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-slate-700 text-right">
                  تاریخ شروع <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                    <CalendarDays size={18} />
                  </span>

                  <DatePicker
                    calendar={persian}
                    locale={persian_fa}
                    value={formData.start_date}
                    onChange={handleStartDateChange}
                    format="YYYY-MM-DD"
                    inputClass={cx(
                      "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 pr-10 text-right text-slate-900",
                      "placeholder:text-slate-400 shadow-sm",
                      "focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary"
                    )}
                    containerClassName="w-full"
                    calendarPosition="bottom-right"
                  />
                </div>
                {fieldError("start_date") ? (
                  <div className="text-rose-700 text-sm mt-1 text-right">
                    {fieldError("start_date")}
                  </div>
                ) : null}
              </div>

              {/* End Date */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-slate-700 text-right">
                  تاریخ پایان
                </label>
                <div className="relative">
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                    <CalendarDays size={18} />
                  </span>

                  <DatePicker
                    calendar={persian}
                    locale={persian_fa}
                    value={formData.end_date}
                    onChange={handleEndDateChange}
                    format="YYYY-MM-DD"
                    inputClass={cx(
                      "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 pr-10 text-right text-slate-900",
                      "placeholder:text-slate-400 shadow-sm",
                      "focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary"
                    )}
                    containerClassName="w-full"
                    calendarPosition="bottom-right"
                  />
                </div>
                {fieldError("end_date") ? (
                  <div className="text-rose-700 text-sm mt-1 text-right">
                    {fieldError("end_date")}
                  </div>
                ) : null}
              </div>

              {/* Summary */}
              <div className="md:col-span-2 space-y-1.5">
                <label className="block text-sm font-medium text-slate-700 text-right">
                  خلاصه وکالت <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute right-3 top-3 text-slate-400">
                    <FileText size={18} />
                  </span>
                  <textarea
                    name="representation_summary"
                    value={formData.representation_summary}
                    onChange={handleChange}
                    required
                    placeholder="خلاصه‌ای از وکالت را وارد کنید"
                    className={cx(
                      "w-full min-h-[120px] rounded-xl border border-slate-200 bg-white px-3 py-2.5 pr-10 text-right text-slate-900",
                      "placeholder:text-slate-400 shadow-sm resize-y",
                      "focus:outline-none focus:ring-2 focus:ring-primary/25 focus:border-primary"
                    )}
                  />
                </div>
                {fieldError("representation_summary") ? (
                  <div className="text-rose-700 text-sm mt-1 text-right">
                    {fieldError("representation_summary")}
                  </div>
                ) : null}
              </div>
            </div>

            {/* Switches + file */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* another_deligation */}
              <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-right">
                    <div className="text-sm font-semibold text-slate-800">
                      توکل به غیر
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      در صورت امکان توکیل به غیر، فعال کنید.
                    </div>
                  </div>

                  <label className="relative inline-flex items-center cursor-pointer select-none">
                    <input
                      type="checkbox"
                      id="another_deligation"
                      name="another_deligation"
                      checked={!!formData.another_deligation}
                      onChange={handleChange}
                      className="sr-only peer"
                    />
                    <span
                      className="
                        relative w-11 h-6 rounded-full bg-slate-200 transition-colors
                        peer-checked:bg-primary
                        after:content-[''] after:absolute after:top-0.5 after:right-0.5 after:h-5 after:w-5
                        after:rounded-full after:bg-white after:shadow after:transition-transform
                        peer-checked:after:-translate-x-5
                      "
                    />
                    <span className="mr-3 text-sm font-medium text-slate-700">
                      فعال
                    </span>
                  </label>
                </div>
              </div>

              {/* representor_dismissal */}
              <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-right">
                    <div className="text-sm font-semibold text-slate-800">
                      عزل وکیل
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      در صورت امکان عزل، فعال کنید.
                    </div>
                  </div>

                  <label className="relative inline-flex items-center cursor-pointer select-none">
                    <input
                      type="checkbox"
                      id="representor_dismissal"
                      name="representor_dismissal"
                      checked={!!formData.representor_dismissal}
                      onChange={handleChange}
                      className="sr-only peer"
                    />
                    <span
                      className="
                        relative w-11 h-6 rounded-full bg-slate-200 transition-colors
                        peer-checked:bg-primary
                        after:content-[''] after:absolute after:top-0.5 after:right-0.5 after:h-5 after:w-5
                        after:rounded-full after:bg-white after:shadow after:transition-transform
                        peer-checked:after:-translate-x-5
                      "
                    />
                    <span className="mr-3 text-sm font-medium text-slate-700">
                      فعال
                    </span>
                  </label>
                </div>
              </div>

              {/* File */}
              <div className="md:col-span-2 rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="text-right">
                    <div className="text-sm font-semibold text-slate-800">
                      فایل پیوست
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      در صورت انتخاب فایل جدید، جایگزین فایل قبلی خواهد شد.
                    </div>
                  </div>
                  <div className="text-slate-400 mt-0.5">
                    <FileUp size={18} />
                  </div>
                </div>

                <div className="mt-3">
                  <UploadInputInterface
                    file={formData.file}
                    onFileChange={handleFileChange}
                  />
                </div>

                {fieldError("file") ? (
                  <div className="text-rose-700 text-sm mt-1 text-right">
                    {fieldError("file")}
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-5 sm:px-7 py-4 border-t border-slate-200 bg-white">
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
              <button
                type="button"
                onClick={() => navigate("/representations")}
                className="inline-flex items-center justify-center rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition"
                disabled={submitting}
              >
                انصراف
              </button>

              <button
                type="submit"
                disabled={submitting}
                className={cx(
                  "inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-bold text-white",
                  "bg-primary hover:opacity-95 active:opacity-90 transition shadow-sm",
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              >
                {submitting ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    در حال ذخیره...
                  </>
                ) : (
                  "ذخیره"
                )}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
